import { observer } from 'mobx-react-lite'
import type { JobDetailsViewModel } from '@/features/jobs/details/JobDetailsViewModel'
import { SectionHeadline } from '@/components/SectionHeadline'
import {
  Card,
  CardContent,
  CardContentDecoration,
  CardSubtitle,
  CardTitle,
} from '@/components/Card'
import { Icon } from '@/components/Icon'
import { Avatar, type AvatarSubject } from '@/components/Avatar'
import { PageSection } from '@/features/jobs/details/components/PageLayout'

export interface ProInfoCardProps {
  subject: AvatarSubject
  isOnline: boolean
  testId?: string
  nameTestId?: string
  statusTestId?: string
}

/**
 * Shows info about the current Pro on the job.
 */
export const ProInfoSection = observer(function ProInfoSection({
  subject,
  isOnline,
  testId,
  nameTestId,
  statusTestId,
}: ProInfoCardProps) {
  return (
    <PageSection testId={testId}>
      <SectionHeadline>Your Pro</SectionHeadline>
      <Card>
        <CardContent>
          <CardContentDecoration spacing="md">
            <Avatar
              subject={subject}
              presence={isOnline ? 'online' : 'offline'}
            />
          </CardContentDecoration>
          <CardTitle testId={nameTestId}>{subject.displayName}</CardTitle>
          <CardSubtitle variant="label" testId={statusTestId}>
            {isOnline ? 'Online' : 'Offline'}
          </CardSubtitle>
        </CardContent>
      </Card>
    </PageSection>
  )
})

interface EmptyProSectionProps {
  testId?: string
}

/**
 * Placeholder for the Pro section when there's no Pro on the job.
 */
export function EmptyProSection({ testId }: EmptyProSectionProps) {
  return (
    <PageSection testId={testId}>
      <SectionHeadline>Your Pro</SectionHeadline>
      <Card>
        <CardContent>
          <CardContentDecoration>
            <Icon name="search" variant="surface-background" />
          </CardContentDecoration>
          <CardTitle>Matching in progress</CardTitle>
          <CardSubtitle>
            We're connecting you to a Pro who will best assist with your
            inquiry.
          </CardSubtitle>
        </CardContent>
      </Card>
    </PageSection>
  )
}

interface ProInfoCardContainerProps {
  vm: JobDetailsViewModel
}

/**
 * Pro Info Card Container
 */
export const ProInfoSectionContainer = observer(function ProInfoCardContainer({
  vm,
}: ProInfoCardContainerProps) {
  // TODO: There's a bug with `onBecomeUnobserved` that causes it to not fire
  // when the component is unmounted.
  // https://github.com/mobxjs/mobx/issues/3774
  // Here's a workaround if it's not resolved by the time we ship
  // const [isOnline, setIsOnline] = useState(false)
  // useEffect(() => {
  //   return reaction(
  //     () => vm.job.championPro?.user.isOnline,
  //     (isOnline) => setIsOnline(isOnline ?? false),
  //     { fireImmediately: true },
  //   )
  // }, [vm])

  if (vm.job.championPro) {
    return (
      <ProInfoSection
        subject={vm.job.championPro.user}
        isOnline={vm.job.championPro.user.isOnline}
      />
    )
  }

  return <EmptyProSection />
})
